import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";

import { StretchedLink } from "~/components/UI/StretchedLink";
import { ROUTES } from "~/constants/routing";
import { InsightCategoryChip } from "~/feature/insights/components/InsightCategoryChip";
import { InsightFeaturedChip } from "~/feature/insights/components/InsightFeaturedChip";
import { InsightTagChips } from "~/feature/insights/components/InsightTagChips";
import {
  insightMetaChipsRowSx,
  liftAboveStretchedLinkSx,
} from "~/feature/insights/constants";
import { type IInsight } from "~/feature/insights/types";
import { formatAuthorLine } from "~/feature/insights/utils/formatAuthorLine";
import { mergeInsightTags } from "~/feature/insights/utils/mergeInsightTags";

import * as Styled from "./ArticleCard.styles";

interface IArticleCardProps {
  insight: IInsight;
  variant?: "default" | "compact" | "related";
  showImage?: boolean;
  showTags?: boolean;
}

export const ArticleCard: FC<IArticleCardProps> = ({
  insight,
  variant = "default",
  showImage = false,
  showTags = true,
}) => {
  const { title, categories = [], primaryTag, tags = [], coverImage, author, publishedAt, slug, featured, dek } =
    insight;

  const articleUrl = ROUTES.insight.createURL(slug);

  const articleTags = useMemo(
    () => mergeInsightTags(primaryTag, tags),
    [primaryTag, tags]
  );

  if (variant === "related") {
    return (
      <Styled.Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Styled.RelatedMedia>
          <img src={coverImage?.url ?? undefined} alt={coverImage?.alt} />
        </Styled.RelatedMedia>
        <Styled.RelatedCardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ ...insightMetaChipsRowSx, mb: 0.5 }}>
            {categories.map((category) => (
              <InsightCategoryChip key={category.slug} category={category} />
            ))}
          </Box>
          <Styled.RelatedCardTitle variant="subtitle2" component="h3">
            <StretchedLink to={articleUrl}>{title}</StretchedLink>
          </Styled.RelatedCardTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto" }}>
            {(author?.avatar || author?.name) && (
              <Styled.AuthorAvatar
                src={author.avatar?.url ?? undefined}
                alt={author.avatar?.alt ?? author?.name ?? undefined}
              >
                {author?.name?.slice(0, 1)}
              </Styled.AuthorAvatar>
            )}
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: 11
              }}>
              {formatAuthorLine(author?.name, publishedAt)}
            </Typography>
          </Box>
        </Styled.RelatedCardContent>
      </Styled.Card>
    );
  }

  if (variant === "compact") {
    return (
      <Styled.CompactCard>
        {showImage && coverImage?.url && (
          <Styled.Media>
            <img src={coverImage.url} alt={coverImage.alt} />
          </Styled.Media>
        )}
        <Styled.CompactCardContent>
          <Box sx={{ ...insightMetaChipsRowSx, mb: 0.5 }}>
            {categories.map((category) => (
              <InsightCategoryChip key={category.slug} category={category} />
            ))}
            {featured && <InsightFeaturedChip />}
          </Box>
          {showTags && articleTags.length > 0 && (
            <Box sx={{ mb: 1, ...liftAboveStretchedLinkSx }}>
              <InsightTagChips tags={articleTags} />
            </Box>
          )}
          <Styled.CompactCardTitle variant="subtitle1" component="h3">
            <StretchedLink to={articleUrl}>{title}</StretchedLink>
          </Styled.CompactCardTitle>
          {dek && (
            <Styled.Excerpt
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0, mb: 2 }}
            >
              {dek}
            </Styled.Excerpt>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto" }}>
            {(author?.avatar || author?.name) && (
              <Styled.AuthorAvatar
                src={author.avatar?.url ?? undefined}
                alt={author.avatar?.alt ?? author?.name ?? undefined}
              >
                {author?.name?.slice(0, 1)}
              </Styled.AuthorAvatar>
            )}
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {formatAuthorLine(author?.name, publishedAt)}
            </Typography>
          </Box>
        </Styled.CompactCardContent>
      </Styled.CompactCard>
    );
  }

  return (
    <Styled.Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Styled.Media>
        <img src={coverImage?.url ?? undefined} alt={coverImage?.alt} />
      </Styled.Media>
      <Styled.CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ ...insightMetaChipsRowSx, mb: 1 }}>
          {categories.map((category) => (
            <InsightCategoryChip key={category.slug} category={category} />
          ))}
          {featured && <InsightFeaturedChip />}
        </Box>
        {showTags && articleTags.length > 0 && (
          <Box sx={{ mb: 1, ...liftAboveStretchedLinkSx }}>
            <InsightTagChips tags={articleTags} />
          </Box>
        )}
        <Styled.CardTitle variant="h6" component="h3">
          <StretchedLink to={articleUrl}>{title}</StretchedLink>
        </Styled.CardTitle>
        {dek && (
          <Styled.Excerpt variant="body2" color="text.secondary">
            {dek}
          </Styled.Excerpt>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto", pt: 1.5 }}>
          {(author?.avatar || author?.name) && (
            <Styled.AuthorAvatar
              src={author.avatar?.url ?? undefined}
              alt={author.avatar?.alt ?? author?.name ?? undefined}
            >
              {author?.name?.slice(0, 1)}
            </Styled.AuthorAvatar>
          )}
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            {formatAuthorLine(author?.name, publishedAt)}
          </Typography>
        </Box>
      </Styled.CardContent>
    </Styled.Card>
  );
};
